import * as React from 'react';
import debounce from 'lodash.debounce';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from '@mui/icons-material/Search';
import { Grid, InputAdornment, Stack } from '@mui/material';
import SearchListGrid from '../searchListGrid/SearchListGrid';
import { IInsituExtractor, IInsituExtractorListParams } from '../../../store/insituExtractor/types';
import { HeadCell, Order } from '../tableGrid/types';
import EnhancedTable from '../tableGrid/tableGrid';
import EnhancedTableToolbar from '../tableGrid/tableToolbar';
import EnhancedTableHead from '../tableGrid/tableHead';
import Row from '../tableGrid/tableRow';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import CustomizedDialogs from '../../atoms/modal/customizedDialogs';
import { SEARCH_DEBOUNCE_TIME_MILLIS } from "../../../constants/common";
import { deleteInsituExtractorPendingSelector, deleteInsituExtractorSelector, getInsituListPendingSelector, getInsituListSelector } from '../../../store/insituExtractor/selectors';
import { deleteInsituExtractorReq, fetchInsituExtractorListReq } from '../../../store/insituExtractor/actions';
// settings data

const headCells: HeadCell[] = [
    {
        id: 'datasetName',
        numeric: false,
        disablePadding: false,
        label: 'Dataset Name',
        width: "160px",
    },
    {
        id: 'createdBy',
        numeric: false,
        disablePadding: false,
        label: 'Username',
        width: "150px",
    },
    {
        id: 'criticalityDescription',
        numeric: false,
        disablePadding: false,
        label: 'Criticality Level',
        width: "80px",
    }
];

const filterCells: string[] = [
    'datasetName', 'username'
]

interface InsituExplorerProps {
    isAdmin: boolean;
};


const InsituExplorer = (props: InsituExplorerProps) => {

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isAdmin, ...other } = props;
    const deletePendingInsitu = useSelector(deleteInsituExtractorPendingSelector);
    const deleteInsitu = useSelector(deleteInsituExtractorSelector)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dataRows, setDataRows] = useState<IInsituExtractor[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [order, setOrder] = useState<Order>("asc");
    const [orderBy, setOrderBy] = useState<keyof IInsituExtractor>("criticality");
    const [selected, setSelected] = useState<readonly string[]>([]);
    const [open, setOpen] = useState(false);
    const [rowId, setRowId] = useState('');
    const [rowDataset, setRowDataset] = useState('')
    const [rowName, setRowName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [deletedIdLoading, setDeletedIdLoading] = useState('')
    const [deletedIds, setDeletedIds] = useState<string[]>([]);

    // Filters-related
    const [usernames, setUsernames] = useState<string[]>([]);
    const [datasets, setDatasets] = useState<string[]>([]);
    const [filters, setFilters] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState(false);

    const selectable: string = "no";
    const allSelectable: boolean = false;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // insitu extractor list    
    const insituList = useSelector(getInsituListSelector);
    const loadingInsituExtractorList = useSelector(getInsituListPendingSelector)

    const params: IInsituExtractorListParams = {
        skip: page * rowsPerPage,
        top: rowsPerPage,
        count: true,
        search: searchTerm,
        datasetName: datasets,
        created_by: usernames,
        orderby: `${orderBy}`,
        order: `${order}`
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        dispatch(fetchInsituExtractorListReq(params));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, page, rowsPerPage, searchTerm, orderBy, order]);


    useEffect(() => {

        let data: IInsituExtractor[] = (insituList.insitu_criticalities);
        const newData: IInsituExtractor[] = data?.map((item: IInsituExtractor) => {
            item.criticalityDescription = "Criticality Level " + item?.criticality;
            return item;
        });
        setDataRows(newData);
        setTotalRows(insituList.count);

    }, [insituList]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property as keyof IInsituExtractor);
        dispatch(fetchInsituExtractorListReq(params));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = dataRows.map((n: { createdBy: any; }) => n.createdBy);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const deleteAllClick = () => {
        setSelected([]);
    }

    useEffect(() => {
        dispatch(fetchInsituExtractorListReq(params));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterResults = (event: React.MouseEvent<HTMLButtonElement>, filters: string[]) => {

        let newDatasets: string[] = [];
        let newUsernames: string[] = [];


        filters.forEach(item => {
            let filter: string[] = item.split(": ");
            switch (filter[0]) {
                case 'datasetName':
                    newDatasets.push(filter[1]);
                    setDatasets(newDatasets);
                    params.datasetName = newDatasets;
                    break;
                case 'username':
                    newUsernames.push(filter[1]);
                    setUsernames(newUsernames);
                    params.created_by = newUsernames;
                    break;
                default:
                    break;
            }
        });

        setFilters(filters);
        setOpenFilters(true);
        setPage(0);

        dispatch(fetchInsituExtractorListReq(params));

    }

    const refreshPage = () => {

        setDatasets([]);
        params.datasetName = [];

        setUsernames([]);
        params.created_by = [];

        setFilters([]);
        setOpenFilters(false);

        setOrder('asc')
        setOrderBy("criticality")

        dispatch(fetchInsituExtractorListReq(params));

    }

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPage(0);
        setSearchTerm(e.target.value);

    }

    const handleSearchDebounce = useMemo(() => {
        return debounce(handleSearch, SEARCH_DEBOUNCE_TIME_MILLIS);
    }, []);

    const searchEl = () => {
        return (
            <TextField
                id="insitu-extractor-search"
                label="Search Insitu Criticality"
                type="search"
                fullWidth={true}
                onChange={handleSearchDebounce}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
            />
        );
    };

    const handleInsituExtractorView = (row: IInsituExtractor, event: React.MouseEvent<HTMLButtonElement>) => {
        const url = `/ViewInsituExtractor/${row.id}`;
        if (event.ctrlKey) {
            window.open(url, '_blank')
        }
        else {
            navigate(url);
        }
    }
    const handleInsituExtractorEdit = (row: IInsituExtractor, event: React.MouseEvent<HTMLButtonElement>) => {
        const url = `/EditInsituExtractor/${row.id}`;
        if (event.ctrlKey) {
            window.open(url, '_blank');
        }
        else {
            navigate(url);
        }
    }

    const confirmInsituExtractorDelete = (row: IInsituExtractor) => {
        setOpen(true);
        setRowId(row.id.toString());
        setRowName(row.datasetName);
        setRowDataset(row.datasetName)
    }

    const handleInistuExtractorDelete = () => {
        setOpen(false);
        setDeletedIds(item => [...item, rowId.toString()]);
        setDeletedIdLoading(rowId);
        setDeletedIds(x => [...x, rowId]);
        dispatch(deleteInsituExtractorReq(rowId))
        dispatch(fetchInsituExtractorListReq(params))

    }

    const handleClose = () => {
        setOpen(false);
    }

    const tableToolBar = () => {
        return (
            <EnhancedTableToolbar numSelected={selected.length} title={'Insitu Extractor'} headCells={headCells} filterCells={filterCells} deleteAll={deleteAllClick}
                filterResults={filterResults} refreshPage={refreshPage} currentFilters={filters} openToolbar={openFilters} />
        );
    }

    const tableHeader = () => {
        return (
            <EnhancedTableHead
                headCells={headCells}
                numSelected={selected.length}
                selectable={allSelectable}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={totalRows}
            />
        );
    }

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRows) : 0;

    const tableRows = () => {

        return (
            <>
                {dataRows?.length > 0 && dataRows.slice()
                    .map((row: IInsituExtractor, index: number) => {
                        const isItemSelected = isSelected(row.datasetName);
                        return (
                            <Row row={row}
                                key={row.id}
                                headCells={headCells}
                                selectable={selectable}
                                canEdit={isAdmin}
                                canDelete={isAdmin}
                                canView={true}
                                canExpand={false}
                                isItemSelected={isItemSelected}
                                handleView={handleInsituExtractorView}
                                handleEdit={handleInsituExtractorEdit}
                                handleDelete={confirmInsituExtractorDelete}
                                loadingIcon={deletePendingInsitu}
                                deleteId={deleteInsitu}
                                deletedRows={deletedIds}
                                deletedIdLoading={deletedIdLoading}
                            >
                            </Row>
                        );
                    })
                }
                {emptyRows > 0 && emptyRows < 10 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={6} />
                    </TableRow>
                )}
                {dataRows?.length < 1 && (
                    <TableRow
                        style={{
                            height: (53) * emptyRows,
                        }}
                    >
                        <TableCell colSpan={6}>There are no insitu Extractors available.</TableCell>
                    </TableRow>
                )}

            </>
        );

    }


    const listGrid = () => {

        return (
            <>
                {/* adding skeleton screen on loading */}
                {loadingInsituExtractorList ?
                    <Grid container width="100%">
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                        <Grid item xs={12}>
                            <Skeleton animation="wave" height={60} />
                        </Grid>
                    </Grid>

                    :
                    <EnhancedTable
                        totalRows={totalRows}
                        tableToolBar={tableToolBar()}
                        tableHeader={tableHeader()}
                        tableRows={tableRows()}
                        pagination={true}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                }
            </>
        );
    }

    return (
        <>
            <SearchListGrid searchEl={searchEl()} children={listGrid()} />

            <CustomizedDialogs
                dialogHeight=''
                modalSize="md"
                key="target"
                modalTitle="Delete Confirmation"
                isOpen={open}
                onClose={handleClose}
                footerAction={
                    <Stack direction="row" spacing={2}>
                        <Button id="266810ee-717e-4ca2-9b83-86ab13a0d778" color="primary" onClick={handleInistuExtractorDelete} autoFocus>
                            Yes
                        </Button>
                        <Button id="16468233-559d-4114-872d-63235116bd40" color="primary" onClick={handleClose}>
                            No
                        </Button>
                    </Stack>
                }
            >
                <> Are you sure you want to delete {rowName}?</>
            </CustomizedDialogs>
        </>
    );

};

export default InsituExplorer;